import React, { useState, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store';
import { addStudyPlan, updateStudyPlan, deleteStudyPlan, setFirebasePlans, syncPlanWithFirebase } from '../store/slices/studySlice';
import { Calendar, Clock, BookOpen, Plus, Save, Trash2, Edit3, ExternalLink, CheckCircle, Circle } from 'lucide-react';
import { questionBankService, QuestionBank } from '../services/questionBankService';
import { studyPlanService, StudyPlan as FirebaseStudyPlan, authService } from '../services/firebase';

// 8. sınıf LGS konuları
const LGS_SUBJECTS = {
  matematik: {
    name: 'Matematik',
    color: 'bg-blue-500',
    topics: [
      'Çarpanlar ve Katlar',
      'Üslü İfadeler',
      'Kareköklü İfadeler',
      'Veri Analizi',
      'Olasılık',
      'Cebirsel İfadeler',
      'Eşitlik ve Denklem',
      'Çember ve Daire',
      'Prizmalar',
      'Piramit, Koni, Küre'
    ]
  },
  turkce: {
    name: 'Türkçe',
    color: 'bg-red-500',
    topics: [
      'Sözcükte Anlam',
      'Cümlede Anlam',
      'Paragrafta Anlam',
      'Yazım Kuralları',
      'Noktalama İşaretleri',
      'Fiilimsiler',
      'Cümle Bilgisi',
      'Metin Türleri',
      'Tema ve Ana Düşünce',
      'Şiir İncelemesi'
    ]
  },
  fen: {
    name: 'Fen Bilimleri',
    color: 'bg-green-500',
    topics: [
      'Mevsimler ve İklim',
      'DNA ve Genetik Kod',
      'Basit Makineler',
      'Işık ve Ses',
      'Madde ve Endüstri',
      'Asit, Baz ve Tuz',
      'Elektrik Yükleri',
      'Enerji Dönüşümleri',
      'Doğal Süreçler',
      'Çevre ve İnsan'
    ]
  },
  sosyal: {
    name: 'Sosyal Bilgiler',
    color: 'bg-purple-500',
    topics: [
      'Osmanlı Devleti',
      'Avrupa ve Osmanlı',
      'Değişim ve Süreklilik',
      'Milli Mücadele',
      'Atatürk İlkeleri',
      'Türkiye Cumhuriyeti',
      'Demokrasi ve Milli Egemenlik',
      'Ekonomi ve Sosyal Hayat',
      'Türkiye ve Dünya',
      'Çağdaş Türkiye'
    ]
  },
  ingilizce: {
    name: 'İngilizce',
    color: 'bg-yellow-500',
    topics: [
      'Friendship',
      'Teen Life',
      'In the Kitchen',
      'On the Phone',
      'The Internet',
      'Adventures',
      'Tourism',
      'Chores',
      'Science',
      'Natural Forces'
    ]
  },
  din: {
    name: 'Din Kültürü',
    color: 'bg-indigo-500',
    topics: [
      'İslam Dini',
      'Hz. Muhammed',
      'Kur\'an-ı Kerim',
      'İbadetler',
      'Ahlak',
      'İslam Tarihi',
      'Peygamberler',
      'Dini Bayramlar',
      'Toplumsal Değerler',
      'Hoşgörü'
    ]
  }
};

// Saat aralıkları
const TIME_SLOTS = [
  '08:00-09:00',
  '09:00-10:00',
  '10:00-11:00',
  '11:00-12:00',
  '13:00-14:00',
  '14:00-15:00',
  '15:00-16:00',
  '16:00-17:00',
  '17:00-18:00',
  '18:00-19:00',
  '19:00-20:00',
  '20:00-21:00'
];

// Günler
const DAYS = [
  { key: 'pazartesi', name: 'Pazartesi' },
  { key: 'sali', name: 'Salı' },
  { key: 'carsamba', name: 'Çarşamba' },
  { key: 'persembe', name: 'Perşembe' },
  { key: 'cuma', name: 'Cuma' },
  { key: 'cumartesi', name: 'Cumartesi' },
  { key: 'pazar', name: 'Pazar' }
];

interface StudySlot {
  id: string;
  day: string;
  time: string;
  subject: string;
  topic: string;
  questionBank?: string; // Soru bankası ID'si
  questionBankName?: string; // Soru bankası adı
  videoLink?: string; // Video linki
  notes?: string; // Notlar
  isCompleted: boolean; // Tamamlanma durumu
  completedAt?: string; // Tamamlanma tarihi
}

interface WeeklyPlan {
  id: string;
  userId: string;
  title: string;
  description?: string;
  subjects: string[];
  startDate: string;
  endDate: string;
  dailyGoal: number;
  weeklyGoal: number;
  isActive: boolean;
  slots: StudySlot[];
  progress: {
    completedDays: number;
    totalDays: number;
    completedHours: number;
    targetHours: number;
  };
  createdAt: string;
  updatedAt: string;
}

// Firestore'a gönderirken undefined değerleri kaldırmak için yardımcı fonksiyon
const sanitizeSlotsForFirestore = (slots: StudySlot[]): any[] => {
  return slots.map((slot) => {
    const s: any = { ...slot };
    Object.keys(s).forEach((key) => {
      if (s[key] === undefined) {
        delete s[key];
      }
    });
    return s;
  });
};

const StudyPlans: React.FC = () => {
  const dispatch = useDispatch();
  const plans = useSelector((state: RootState) => state.study.plans);
  
  const [firebasePlans, setFirebasePlans] = useState<WeeklyPlan[]>([]);
  const [currentPlan, setCurrentPlan] = useState<WeeklyPlan | null>(null);
  const [planName, setPlanName] = useState('');
  const [planDescription, setPlanDescription] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<{day: string, time: string} | null>(null);
  const [editingSlot, setEditingSlot] = useState<StudySlot | null>(null);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');
  const [selectedQuestionBank, setSelectedQuestionBank] = useState('');
  const [videoLink, setVideoLink] = useState('');
  const [notes, setNotes] = useState('');
  const [questionBanks, setQuestionBanks] = useState<QuestionBank[]>([]);
  const [loadingQuestionBanks, setLoadingQuestionBanks] = useState(false);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [savingPlan, setSavingPlan] = useState(false);

  // Soru bankalarını yükle
  useEffect(() => {
    const loadQuestionBanks = async () => {
      try {
        setLoadingQuestionBanks(true);
        const currentUser = authService.getCurrentUser();
        if (!currentUser) {
          console.warn('Soru bankaları için kullanıcı oturumu yok, yükleme atlandı');
          return;
        }
        const banks = await questionBankService.getQuestionBanks();
        setQuestionBanks(banks);
      } catch (error) {
        console.error('Soru bankaları yüklenirken hata:', error);
      } finally {
        setLoadingQuestionBanks(false);
      }
    };

    loadQuestionBanks();
  }, []);

  // Firebase'den çalışma planlarını yükle
  useEffect(() => {
    const loadFirebasePlans = async () => {
      try {
        const currentUser = authService.getCurrentUser();
        if (!currentUser) return;

        setLoadingPlans(true);
        const plans = await studyPlanService.getUserPlans(currentUser.uid);
        
        // Firebase planlarını WeeklyPlan formatına dönüştür
        const formattedPlans: WeeklyPlan[] = plans.map(plan => ({
          ...plan,
          slots: plan.slots || [] // slots alanını ekle
        }));
        
        setFirebasePlans(formattedPlans);
        
        // Redux store'u da güncelle
        dispatch(setFirebasePlans(formattedPlans));
      } catch (error) {
        console.error('Çalışma planları yüklenirken hata:', error);
      } finally {
        setLoadingPlans(false);
      }
    };

    loadFirebasePlans();
  }, []);

  // Mevcut planı yükle
  const loadPlan = (plan: any) => {
    console.log('loadPlan çağrıldı:', plan);
    console.log('plan.id:', plan.id);
    
    // Gelen plan objesini WeeklyPlan yapısına uyarlayıp atayalım
    setCurrentPlan({
      ...plan,
      title: plan.title || plan.name, // Title alanını garanti altına al
      slots: plan.slots || [], // Mevcut slots'u koru, yoksa boş array
      createdAt: plan.createdAt || new Date().toISOString()
    });
  };

  // Yeni plan oluştur
  const createNewPlan = async () => {
    if (!planName.trim()) return;
    
    try {
      const currentUser = authService.getCurrentUser();
      if (!currentUser) {
        console.error('Kullanıcı oturum açmamış');
        return;
      }

      setSavingPlan(true);
      
      const newPlanData = {
        userId: currentUser.uid,
        title: planName,
        description: planDescription,
        subjects: [],
        startDate: new Date().toISOString(),
        endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 1 hafta sonra
        dailyGoal: 4, // Varsayılan günlük hedef
        weeklyGoal: 28, // Varsayılan haftalık hedef
        isActive: true,
        progress: {
          completedDays: 0,
          totalDays: 7,
          completedHours: 0,
          targetHours: 28
        }
      };
      
      const created = await studyPlanService.createPlan(newPlanData);

      // createPlan bazı yerlerde sadece ID döndürüyor olabilir. Güvenli şekilde objeye dönüştürelim
      const nowIso = new Date().toISOString();
      const createdPlanObj: WeeklyPlan = typeof created === 'string'
        ? {
            id: created,
            userId: newPlanData.userId,
            title: newPlanData.title,
            description: newPlanData.description,
            subjects: newPlanData.subjects,
            startDate: newPlanData.startDate,
            endDate: newPlanData.endDate,
            dailyGoal: newPlanData.dailyGoal,
            weeklyGoal: newPlanData.weeklyGoal,
            isActive: newPlanData.isActive,
            slots: [],
            progress: newPlanData.progress,
            createdAt: nowIso,
            updatedAt: nowIso
          }
        : {
            // createPlan obje döndürdüyse onu WeeklyPlan formatına tamamlayalım
            ...(created as any),
            title: (created as any).title || (created as any).name,
            slots: (created as any).slots || [],
            createdAt: (created as any).createdAt || nowIso,
            updatedAt: (created as any).updatedAt || nowIso
          };
      
      // Firebase planlarını güncelle
      setFirebasePlans(prev => [...prev, createdPlanObj]);
      setCurrentPlan(createdPlanObj);
      
      // Redux store'u da güncelle
      dispatch(syncPlanWithFirebase(createdPlanObj));
      
      setPlanName('');
      setPlanDescription('');
      setShowCreateModal(false);
    } catch (error) {
      console.error('Plan oluşturulurken hata:', error);
    } finally {
      setSavingPlan(false);
    }
  };

  // Slot'a konu ekle
  const addTopicToSlot = async () => {
    console.log('addTopicToSlot çağrıldı:', {
      selectedSlot,
      selectedSubject,
      selectedTopic,
      currentPlan,
      currentPlanId: currentPlan?.id,
      hasCurrentPlan: !!currentPlan
    });
    
    if (!selectedSlot || !selectedSubject || !selectedTopic || !currentPlan || !currentPlan.id) {
      console.log('Eksik değerler nedeniyle işlem iptal edildi');
      if (!currentPlan) {
        alert('Lütfen önce bir çalışma planı seçin!');
      }
      return;
    }
    
    try {
      setSavingPlan(true);
      
      const selectedBank = questionBanks.find(bank => bank.id === selectedQuestionBank);
      
      const newSlot: StudySlot = {
        id: Date.now().toString(),
        day: selectedSlot.day,
        time: selectedSlot.time,
        subject: selectedSubject,
        topic: selectedTopic,
        questionBank: selectedQuestionBank || undefined,
        questionBankName: selectedBank?.name || undefined,
        videoLink: videoLink || undefined,
        notes: notes || undefined,
        isCompleted: false
      };
      
      const updatedSlots = (currentPlan.slots || []).filter(
        slot => !(slot.day === selectedSlot.day && slot.time === selectedSlot.time)
      );
      
      const sanitizedSlots = sanitizeSlotsForFirestore([...updatedSlots, newSlot]);
      
      const updatedPlan = {
        ...currentPlan,
        slots: sanitizedSlots as any,
        updatedAt: new Date().toISOString()
      };
      
      // Firebase'de güncelle
      await studyPlanService.updatePlan(currentPlan.id, {
        slots: updatedPlan.slots as any,
        updatedAt: updatedPlan.updatedAt
      });
      
      // Local state'i güncelle
      setCurrentPlan(updatedPlan);
      
      // Firebase planlarını güncelle
      setFirebasePlans(prev => 
        prev.map(plan => plan.id === currentPlan.id ? updatedPlan : plan)
      );
      
      // Redux store'u da güncelle
        dispatch(syncPlanWithFirebase(updatedPlan));
      
      resetModalFields();
    } catch (error) {
      console.error('Slot eklenirken hata:', error);
    } finally {
      setSavingPlan(false);
    }
  };

  // Modal alanlarını sıfırla
  const resetModalFields = () => {
    setSelectedSlot(null);
    setEditingSlot(null);
    setSelectedSubject('');
    setSelectedTopic('');
    setSelectedQuestionBank('');
    setVideoLink('');
    setNotes('');
  };

  // Slot düzenleme modalını aç
  const openEditSlot = (slot: StudySlot) => {
    setEditingSlot(slot);
    setSelectedSubject(slot.subject);
    setSelectedTopic(slot.topic);
    setSelectedQuestionBank(slot.questionBank || '');
    setVideoLink(slot.videoLink || '');
    setNotes(slot.notes || '');
  };

  // Slot'ı güncelle
  const updateSlot = async () => {
    if (!editingSlot || !selectedSubject || !selectedTopic || !currentPlan) return;
    
    try {
      setSavingPlan(true);
      
      const selectedBank = questionBanks.find(bank => bank.id === selectedQuestionBank);
      
      const updatedSlot: StudySlot = {
        ...editingSlot,
        subject: selectedSubject,
        topic: selectedTopic,
        questionBank: selectedQuestionBank || undefined,
        questionBankName: selectedBank?.name || undefined,
        videoLink: videoLink || undefined,
        notes: notes || undefined
      };
      
      const updatedSlotsRaw = currentPlan.slots.map(slot => 
        slot.id === editingSlot.id ? updatedSlot : slot
      );
    
      const sanitizedSlots = sanitizeSlotsForFirestore(updatedSlotsRaw);
      
      const updatedPlan = {
        ...currentPlan,
        slots: sanitizedSlots as any,
        updatedAt: new Date().toISOString()
      };
      
      // Firebase'de güncelle
      await studyPlanService.updatePlan(currentPlan.id, {
        slots: updatedPlan.slots as any,
        updatedAt: updatedPlan.updatedAt
      });
      
      // Local state'i güncelle
      setCurrentPlan(updatedPlan);
      
      // Firebase planlarını güncelle
      setFirebasePlans(prev => 
        prev.map(plan => plan.id === currentPlan.id ? updatedPlan : plan)
      );
      
      // Redux store'u da güncelle
      dispatch(syncPlanWithFirebase(updatedPlan));
      
      setEditingSlot(null);
      resetModalFields();
    } catch (error) {
      console.error('Slot güncellenirken hata:', error);
    } finally {
      setSavingPlan(false);
    }
  };

  // Slot tamamlanma durumunu değiştir
  const toggleSlotCompletion = async (slotId: string) => {
    if (!currentPlan) return;
    
    try {
      setSavingPlan(true);
      
      const updatedSlotsRaw = currentPlan.slots.map(slot => {
        if (slot.id === slotId) {
          return {
            ...slot,
            isCompleted: !slot.isCompleted,
            completedAt: !slot.isCompleted ? new Date().toISOString() : undefined
          };
        }
        return slot;
      });
      
      const sanitizedSlots = sanitizeSlotsForFirestore(updatedSlotsRaw);
      
      const updatedPlan = {
        ...currentPlan,
        slots: sanitizedSlots as any,
        updatedAt: new Date().toISOString()
      };
      
      // Firebase'de güncelle
      await studyPlanService.updatePlan(currentPlan.id, {
        slots: updatedPlan.slots as any,
        updatedAt: updatedPlan.updatedAt
      });
      
      // Local state'i güncelle
      setCurrentPlan(updatedPlan);
      
      // Firebase planlarını güncelle
      setFirebasePlans(prev => 
        prev.map(plan => plan.id === currentPlan.id ? updatedPlan : plan)
      );
    } catch (error) {
      console.error('Slot tamamlanma durumu güncellenirken hata:', error);
    } finally {
      setSavingPlan(false);
    }
  };

  // Slot'tan konu sil
  const removeSlot = async (day: string, time: string) => {
    if (!currentPlan) return;
    
    try {
      setSavingPlan(true);
      
      const updatedSlotsRaw = currentPlan.slots.filter(
        slot => !(slot.day === day && slot.time === time)
      );

      const sanitizedSlots = sanitizeSlotsForFirestore(updatedSlotsRaw);
      
      const updatedPlan = {
        ...currentPlan,
        slots: sanitizedSlots as any,
        updatedAt: new Date().toISOString()
      };
      
      // Firebase'de güncelle
      await studyPlanService.updatePlan(currentPlan.id, {
        slots: updatedPlan.slots as any,
        updatedAt: updatedPlan.updatedAt
      });
      
      // Local state'i güncelle
      setCurrentPlan(updatedPlan);
      
      // Firebase planlarını güncelle
      setFirebasePlans(prev => 
        prev.map(plan => plan.id === currentPlan.id ? updatedPlan : plan)
      );
    } catch (error) {
      console.error('Slot silinirken hata:', error);
    } finally {
      setSavingPlan(false);
    }
  };

  // Planı kaydet
  const savePlan = () => {
    if (!currentPlan) return;
    
    const planToSave = {
      id: currentPlan.id,
      title: currentPlan.title,
      slots: currentPlan.slots,
      createdAt: currentPlan.createdAt,
      isActive: true,
      subjects: [...new Set(currentPlan.slots.map(slot => slot.subject))],
      dailyGoal: 2,
      weeklyGoal: 14
    };
    
    const existingPlan = plans.find(p => p.id === currentPlan.id);
    if (existingPlan) {
      dispatch(updateStudyPlan(planToSave));
    } else {
      dispatch(addStudyPlan(planToSave));
    }

    // Kaydetme işlemi tamamlandıktan sonra planlar ekranına dön
    setCurrentPlan(null);
  };

  // Planı sil
  const deletePlan = async (planId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Plan kartına tıklamayı engelle
    if (window.confirm('Bu planı silmek istediğinizden emin misiniz?')) {
      try {
        setSavingPlan(true);
        
        // Firebase'den planı sil
        await studyPlanService.deletePlan(planId);
        
        // Yerel state'den planı kaldır
        setFirebasePlans(prev => prev.filter(plan => plan.id !== planId));
        
        // Eğer silinen plan şu anda aktif plan ise, currentPlan'ı temizle
        if (currentPlan && currentPlan.id === planId) {
          setCurrentPlan(null);
        }
        
        // Redux store'dan da sil (geriye dönük uyumluluk için)
        dispatch(deleteStudyPlan(planId));
      } catch (error) {
        console.error('Plan silinirken hata oluştu:', error);
        alert('Plan silinirken bir hata oluştu. Lütfen tekrar deneyin.');
      } finally {
        setSavingPlan(false);
      }
    }
  };

  // Belirli bir gün ve saatteki konuyu getir
  const getSlotContent = (day: string, time: string) => {
    if (!currentPlan || !currentPlan.slots) return null;
    return currentPlan.slots.find(slot => slot.day === day && slot.time === time);
  };

  return (
    <div className="min-h-screen p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white/10 dark:bg-gray-800/20 backdrop-blur-sm rounded-2xl shadow-lg p-6 mb-6 border border-white/20 dark:border-gray-700/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Çalışma Planım</h1>
                <p className="text-gray-600 dark:text-gray-300">Haftalık ders programını oluştur</p>
              </div>
            </div>
            
            <div className="flex gap-3">
              {!currentPlan && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all duration-200 shadow-lg"
                >
                  <Plus className="w-4 h-4" />
                  Yeni Plan
                </button>
              )}
              
              {currentPlan && (
                <button
                  onClick={savePlan}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all duration-200 shadow-lg"
                >
                  <Save className="w-4 h-4" />
                  Kaydet
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Plan Seçimi */}
        {!currentPlan && (firebasePlans.length > 0 || plans.length > 0) && (
          <div className="bg-white/10 dark:bg-gray-800/20 backdrop-blur-sm rounded-2xl shadow-lg p-6 mb-6 border border-white/20 dark:border-gray-700/30">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Mevcut Planlarım</h2>
            {loadingPlans ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                <span className="ml-2 text-gray-600 dark:text-gray-300">Planlar yükleniyor...</span>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {firebasePlans.map((plan) => {
                const totalSlots = plan.slots?.length || 0;
                const completedSlots = plan.slots?.filter(slot => slot.isCompleted).length || 0;
                const completionPercentage = totalSlots > 0 ? Math.round((completedSlots / totalSlots) * 100) : 0;
                
                return (
                  <div
                    key={plan.id}
                    onClick={() => loadPlan(plan)}
                    className="p-4 bg-white/20 dark:bg-gray-800/30 backdrop-blur-sm rounded-xl border border-white/30 dark:border-gray-600/30 hover:border-blue-300 dark:hover:border-blue-500 cursor-pointer transition-all duration-200 hover:shadow-md relative group"
                  >
                    <h3 className="font-semibold text-gray-800 dark:text-white mb-2">{plan.title || plan.name}</h3>
                    
                    {/* İlerleme Bilgileri */}
                    <div className="space-y-2 mb-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-300">Toplam Ders:</span>
                         <span className="font-medium text-gray-800 dark:text-white">{totalSlots}</span>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-300">Tamamlanan:</span>
                        <span className="font-medium text-green-600">{completedSlots}</span>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-300">Kalan:</span>
                        <span className="font-medium text-orange-600">{totalSlots - completedSlots}</span>
                      </div>
                    </div>
                    
                    {/* İlerleme Çubuğu */}
                    <div className="mb-2">
                      <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-300 mb-1">
                        <span>İlerleme</span>
                        <span>{completionPercentage}%</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-green-500 to-emerald-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${completionPercentage}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    {/* Durum Badge */}
                    <div className="flex items-center gap-2">
                      {completionPercentage === 100 ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                          <CheckCircle className="w-3 h-3" />
                          Tamamlandı
                        </span>
                      ) : completionPercentage > 0 ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                          <Circle className="w-3 h-3" />
                          Devam Ediyor
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-800 text-xs font-medium rounded-full">
                          <Circle className="w-3 h-3" />
                          Başlanmadı
                        </span>
                      )}
                    </div>
                    
                    <button
                      onClick={(e) => deletePlan(plan.id, e)}
                      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 bg-red-500 text-white rounded-lg hover:bg-red-600"
                      title="Planı Sil"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
              </div>
            )}
          </div>
        )}

        {/* Ana Takvim */}
        {currentPlan && (
          <div className="bg-white/10 dark:bg-gray-800/20 backdrop-blur-sm rounded-2xl shadow-lg p-6 border border-white/20 dark:border-gray-700/30">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-800 dark:text-white">{currentPlan.title}</h2>
              <div className="text-sm text-gray-600 dark:text-gray-300">
                Toplam: {currentPlan.slots?.length || 0} ders saati
              </div>
            </div>

            {/* Takvim Tablosu */}
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="p-3 text-left font-semibold text-gray-700 dark:text-gray-300 border-b-2 border-gray-200 dark:border-gray-600">
                      Saat
                    </th>
                    {DAYS.map(day => (
                      <th key={day.key} className="p-3 text-center font-semibold text-gray-700 dark:text-gray-300 border-b-2 border-gray-200 dark:border-gray-600">
                        {day.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {TIME_SLOTS.map(time => (
                    <tr key={time} className="border-b border-gray-100 dark:border-gray-700">
                      <td className="p-3 font-medium text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800">
                        {time}
                      </td>
                      {DAYS.map(day => {
                        const slot = getSlotContent(day.key, time);
                        return (
                          <td key={`${day.key}-${time}`} className="p-2">
                            {slot ? (
                              <div className={`p-3 rounded-lg text-white text-sm relative group ${slot.isCompleted ? 'bg-gray-600' : LGS_SUBJECTS[slot.subject as keyof typeof LGS_SUBJECTS]?.color || 'bg-gray-500'}`}>
                                <div className="flex items-center justify-between mb-1">
                                  <div className="font-semibold text-xs">
                                    {LGS_SUBJECTS[slot.subject as keyof typeof LGS_SUBJECTS]?.name}
                                  </div>
                                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    {slot.videoLink && (
                                      <a
                                        href={slot.videoLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className="p-1 bg-white/20 rounded hover:bg-white/30"
                                        title="Video İzle"
                                      >
                                        <ExternalLink className="w-3 h-3" />
                                      </a>
                                    )}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openEditSlot(slot);
                                      }}
                                      className="p-1 bg-white/20 rounded hover:bg-white/30"
                                      title="Düzenle"
                                    >
                                      <Edit3 className="w-3 h-3" />
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        removeSlot(day.key, time);
                                      }}
                                      className="p-1 bg-white/20 rounded hover:bg-white/30"
                                      title="Sil"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  </div>
                                </div>
                                <div className="text-xs opacity-90 mb-2">
                                  {slot.topic}
                                </div>
                                {slot.questionBankName && (
                                  <div className="text-xs opacity-80 mb-1">
                                    📚 {slot.questionBankName}
                                  </div>
                                )}
                                <div className="flex items-center justify-between">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleSlotCompletion(slot.id);
                                    }}
                                    className="flex items-center gap-1 text-xs hover:bg-white/20 rounded px-1 py-0.5"
                                  >
                                    {slot.isCompleted ? (
                                      <CheckCircle className="w-3 h-3" />
                                    ) : (
                                      <Circle className="w-3 h-3" />
                                    )}
                                    {slot.isCompleted ? 'Tamamlandı' : 'Tamamla'}
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <button
                                onClick={() => setSelectedSlot({day: day.key, time})}
                                className="w-full h-16 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all duration-200 flex items-center justify-center"
                              >
                                <Plus className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                              </button>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Konu Ekleme/Düzenleme Modal */}
        {(selectedSlot || editingSlot) && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl p-4 sm:p-6 w-full max-w-[95vw] sm:max-w-lg md:max-w-xl lg:max-w-2xl max-h-[95vh] overflow-y-auto">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                {editingSlot ? 'Çalışma Slotunu Düzenle' : 
                 `${DAYS.find(d => d.key === selectedSlot?.day)?.name} - ${selectedSlot?.time}`}
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Ders Seç
                  </label>
                  <select
                    value={selectedSubject}
                    onChange={(e) => {
                      setSelectedSubject(e.target.value);
                      setSelectedTopic('');
                    }}
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Ders seçin...</option>
                    {Object.entries(LGS_SUBJECTS).map(([key, subject]) => (
                      <option key={key} value={key}>{subject.name}</option>
                    ))}
                  </select>
                </div>
                
                {selectedSubject && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Konu Seç
                    </label>
                    <select
                      value={selectedTopic}
                      onChange={(e) => setSelectedTopic(e.target.value)}
                      className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">Konu seçin...</option>
                      {LGS_SUBJECTS[selectedSubject as keyof typeof LGS_SUBJECTS]?.topics.map((topic) => (
                        <option key={topic} value={topic}>{topic}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Soru Bankası Seçimi */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Soru Bankası (Opsiyonel)
                  </label>
                  <select
                    value={selectedQuestionBank}
                    onChange={(e) => setSelectedQuestionBank(e.target.value)}
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    disabled={loadingQuestionBanks}
                  >
                    <option value="">Soru bankası seçin...</option>
                    {questionBanks
                      .filter(bank => !selectedSubject || bank.subject === selectedSubject)
                      .map((bank) => (
                        <option key={bank.id} value={bank.id}>
                          {bank.name} - {bank.publisher}
                        </option>
                      ))
                    }
                  </select>
                  {loadingQuestionBanks && (
                    <p className="text-sm text-gray-500 mt-1">Soru bankaları yükleniyor...</p>
                  )}
                </div>

                {/* Video Linki */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Video Linki (Opsiyonel)
                  </label>
                  <input
                    type="url"
                    value={videoLink}
                    onChange={(e) => setVideoLink(e.target.value)}
                    placeholder="https://youtube.com/watch?v=..."
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Notlar */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Notlar (Opsiyonel)
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Bu konu hakkında notlarınız..."
                    rows={3}
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={resetModalFields}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  İptal
                </button>
                <button
                  onClick={editingSlot ? updateSlot : addTopicToSlot}
                  disabled={!selectedSubject || !selectedTopic}
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  {editingSlot ? 'Güncelle' : 'Ekle'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Plan Oluşturma Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl p-4 sm:p-6 w-full max-w-[95vw] sm:max-w-md md:max-w-lg">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                Yeni Plan Oluştur
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Plan Adı
                  </label>
                  <input
                    type="text"
                    value={planName}
                    onChange={(e) => setPlanName(e.target.value)}
                    placeholder="Örn: LGS Hazırlık Planım"
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  İptal
                </button>
                <button
                  onClick={createNewPlan}
                  disabled={!planName.trim()}
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  Oluştur
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudyPlans;